import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Popconfirm, Space, Table, Typography } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import CustomerSearch from '../../components/ui/CustomerSearch';
import SearchInput from '../../components/ui/SearchInput';
import { deleteOrder, getOrders } from '../../redux/actions/order';
import { setOrders } from '../../redux/slices/orderSlice';
import { store } from '../../redux/store';
import { getRoutes } from '../../routes/Routes';
import { formatCurrency } from '../../utils';

const Orders = () => {
    const navigate = useNavigate();
    const { orders } = useSelector((state) => state.orderSlice);
    const [search, setSearch] = useState('');
    const [customer, setCustomer] = useState(null);


    const fetchOrders = useDebouncedCallback(async (params = {}) => {
        const res = await getOrders(params);
        if (res?.ack) {
            store.dispatch(setOrders(res?.orders || []));
        }
    }, 400);

    const handleDelete = async (record) => {
        const res = await deleteOrder(record?.id);
        if (res?.ack) {
            fetchOrders();
        }
    };

    const columns = [
        {
            title: 'Order Code',
            dataIndex: 'code',
            key: 'code',
        },
        {
            title: 'Customer Code',
            dataIndex: 'customer_code',
            key: 'customer_code',
        },
        { title: 'Customer Name', dataIndex: 'customer_name', key: 'customer_name' },
        {
            title: 'Order Date',
            dataIndex: 'order_date',
            key: 'order_date',
            render: (text) => text && dayjs(text).format('DD-MM-YYYY'),
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            render: (value) => {
                if (value === 1) return 'Collection';
                if (value === 2) return 'Delivery';
                return '-';
            }
        },
        {
            title: 'Total Amount',
            dataIndex: 'order_value',
            key: 'order_value',
            render: (text) => text && formatCurrency(text),
        },
        {
            title: 'Actions',
            key: 'actions',
            align: 'end',
            render: (_, record) => (
                <Space size="small">
                    <Button
                        color="primary"
                        variant="filled"
                        onClick={() => navigate(getRoutes().order_details.route(record?.id))}
                        icon={<EditOutlined />}
                    />
                    <Popconfirm
                        title="Are you sure to delete this order?"
                        description="This action cannot be undone."
                        okText="Yes, Delete"
                        cancelText="Cancel"
                        okType="danger"
                        onConfirm={() => handleDelete(record)}
                    >
                        <Button color="danger" variant="filled" icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const handleSearchChange = (value) => {
        setSearch(value);
        fetchOrders({ searchText: value, customer_netsuite_id: customer });
    };

    const handleCustomerChange = (value) => {
        setCustomer(value);
        fetchOrders({ searchText: search, customer_netsuite_id: value });
    }

    useEffect(() => {
        fetchOrders();
    }, []);

    return (
        <div className="order-page">
            <div className="page-header mb-3">
                <div className="flex justify-content-between align-items-center mb-2">
                    <Typography.Title level={3} className='page-title'>Orders Management</Typography.Title>
                    <Button
                        className="shadow-2"
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => navigate(getRoutes().create_order.route())}
                    >
                        New Order
                    </Button>
                </div>

                <div className="flex align-items-center gap-2 flex-wrap">
                    <SearchInput maxWidth="25rem" onSearch={handleSearchChange} />
                    <div className="max-w-25rem w-full">
                        <CustomerSearch
                            size="large"
                            placeholder="Search customer..."
                            showUnassigned={false}
                            onChange={handleCustomerChange}
                            value={customer}
                        />
                    </div>
                </div>
            </div>

            <Table
                size="small"
                columns={columns}
                rowKey={(record) => record?.id}
                dataSource={[...orders]?.reverse()}
                pagination={false}
                scroll={{ x: 'max-content' }}
            />
        </div>
    );
};

export default Orders;
